import React, { useEffect, useState } from 'react'
import CMS from '../../../components/cms/CMS'
import PageBanner from '../../../components/general/PageBanner'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import { fetchShowcaseGet } from '../../../redux/slices/showcaseGetSlice'
import { useParams } from 'react-router-dom'
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor } from "@dnd-kit/core"
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Button, Card, Select, notification } from 'antd'
import { URL } from '../../../api'
import { fetchShowcaseDesign } from '../../../redux/slices/showcaseDesignSlice'

const ContentElement = ({ item, elementType, lang, onColChange }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging }
        = useSortable({ id: elementType, transition: { duration: 150, easing: 'cubic-bezier(0.25, 1, 0.5, 1)', } });

    const elementData = {
        title: {
            value: item?.title?.[lang] || "",
            col: item?.titleCol || 12,
            sort: (item?.titleSort ?? 1) + 1,
            className: item?.titleClassname || "",
            label: "Title"
        },
        description: {
            value: item?.description?.[lang] || "",
            col: item?.descriptionCol || 12,
            sort: (item?.descriptionSort ?? 2) + 1,
            className: item?.titleClassname || "",
            label: "Description"
        },
        content: {
            value: item?.content?.[lang] || "",
            col: item?.contentCol || 12,
            sort: (item?.contentSort ?? 2) + 3,
            className: item?.titleClassname || "",
            label: "Content"
        },
        specialField1: {
            value: item?.specialField1?.[lang] || "",
            col: item?.specialField1Col || 12,
            sort: (item?.specialField1Sort ?? 4) + 5,
            className: item?.titleClassname || "",
            label: "Special Field 1"
        },
        specialField2: {
            value: item?.specialField2?.[lang] || "",
            col: item?.specialField2Col || 12,
            sort: (item?.specialField2Sort ?? 5) + 6,
            className: item?.titleClassname || "",
            label: "Special Field 2"
        },
        specialField3: {
            value: item?.specialField3?.[lang] || "",
            col: item?.specialField3Col || 12,
            sort: (item?.specialField3Sort ?? 6) + 7,
            className: item?.titleClassname || "",
            label: "Special Field 3"
        },
        specialField4: {
            value: item?.specialField4?.[lang] || "",
            col: item?.specialField4Col || 12,
            sort: (item?.specialField4Sort ?? 7) + 8,
            className: item?.titleClassname || "",
            label: "Special Field 4"
        },
        specialField5: {
            value: item?.specialField5?.[lang] || "",
            col: item?.specialField5Col || 12,
            sort: (item?.specialField5Sort ?? 8) + 9,
            className: item?.titleClassname || "",
            label: "Special Field 5"
        }
    };

    const data = elementData[elementType];
    if (!data) return null;
    if (!data.value) return null;

    const handleColChange = (value) => {
        onColChange(elementType, value);
    };

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        border: isDragging ? "2px dashed #1890ff" : "1px solid #e8e8e8",
        borderRadius: "8px",
        backgroundColor: "#fff",
        opacity: isDragging ? 0.8 : 1,
        cursor: "grab",
        padding: "0",
        height: "100%"
    };

    const renderContent = () => {
        switch (elementType) {
            case 'title':
                return <h2>{data.value}</h2>;
            case 'description':
                return <p>{data.value}</p>;
            case 'content':
                return <div dangerouslySetInnerHTML={{ __html: data.value }} />;
            default:
                return <p>{data.value}</p>;
        }
    };

    return (
        <div className={`col-md-${data.col} mb-4`}>
            <div ref={setNodeRef} style={style} className="position-relative h-100">
                <Card
                    title={
                        <div className="d-flex align-items-center justify-content-between">
                            <div {...attributes}{...listeners} className="drag-handle mr-2">☰</div>
                        </div>
                    }
                    extra={
                        <Select value={data.col} onChange={handleColChange} onClick={e => e.stopPropagation()}>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(value => (
                                <Select.Option key={value} value={value}>Col-{value}</Select.Option>
                            ))}
                        </Select>
                    }
                    bordered
                    className="h-100"
                >
                    <div className="element-content">{renderContent()}</div>
                </Card>
            </div>
        </div>
    );
};

const FileElement = ({ item, index, fileId, onColChange }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging }
        = useSortable({
            id: fileId,
            transition: {
                duration: 150,
                easing: 'cubic-bezier(0.25, 1, 0.5, 1)'
            }
        });

    const filePath = item.files[index];
    const col = item.filesCol && item.filesCol[index] ? item.filesCol[index] : 12;
    const sort = item.filesSort && item.filesSort[index] ? item.filesSort[index] + 1 : index + 5;

    const handleColChange = (value) => {
        onColChange(fileId, value);
    };

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        border: isDragging ? "2px dashed #1890ff" : "1px solid #e8e8e8",
        borderRadius: "8px",
        backgroundColor: "#fff",
        opacity: isDragging ? 0.8 : 1,
        cursor: "grab",
        padding: "0",
        height: "100%"
    };

    return (
        <div className={`col-md-${col} mb-4`}>
            <div ref={setNodeRef} style={style} className="position-relative h-100">
                <Card
                    title={
                        <div className="d-flex align-items-center justify-content-between">
                            <div {...attributes}{...listeners} className="drag-handle mr-2">☰ Resim {index + 1}</div>
                        </div>
                    }
                    extra={
                        <Select value={col} onChange={handleColChange} onClick={e => e.stopPropagation()}>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(value => (
                                <Select.Option key={value} value={value}>Col-{value}</Select.Option>
                            ))}
                        </Select>
                    }
                    bordered
                    className="h-100"
                >
                    <div className="element-content">
                        <img src={`${URL}${filePath}`} alt="" className="img-fluid" />
                    </div>
                </Card>
            </div>
        </div>
    );
};

const PageShowcaseDesign = () => {
    const { t } = useTranslation()
    const showcase = useSelector((state) => state.showcaseGet.data)
    const lang = useSelector((state) => state.lang.lang)
    const dispatch = useDispatch()
    const { id } = useParams()
    const [updatedData, setUpdatedData] = useState(null)
    const [saving, setSaving] = useState(false)

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    const hasContent = (data, field) => {
        if (field === 'files') {
            return Array.isArray(data.files) && data.files.length > 0;
        }
        return data[field] && Object.keys(data[field]).length > 0;
    };

    const getData = async () => {
        await dispatch(fetchShowcaseGet({ id: id }))
    }

    useEffect(() => { getData() }, [dispatch, id])

    useEffect(() => {
        if (showcase) {
            const initData = { ...showcase };

            if (hasContent(initData, 'title')) {
                initData.titleSort = initData.titleSort ?? 1;
                if (initData.titleCol === null) {
                    initData.titleCol = 12;
                }
            }

            if (hasContent(initData, 'description')) {
                initData.descriptionSort = initData.descriptionSort ?? 2;
                if (initData.descriptionCol === null) {
                    initData.descriptionCol = 12;
                }
            }

            if (hasContent(initData, 'content')) {
                initData.contentSort = initData.contentSort ?? 3;
                if (initData.contentCol === null) {
                    initData.contentCol = 12;
                }
            }

            if (hasContent(initData, 'files')) {
                if (!Array.isArray(initData.filesSort) || initData.filesSort.length === 0) {
                    initData.filesSort = initData.files.map((_, index) => 4 + index);
                } else if (initData.filesSort.length < initData.files.length) {
                    const currentLength = initData.filesSort.length;
                    for (let i = currentLength; i < initData.files.length; i++) {
                        initData.filesSort.push(4 + i);
                    }
                }

                if (!Array.isArray(initData.filesCol) || initData.filesCol.length === 0) {
                    initData.filesCol = initData.files.map(() => 12);
                } else if (initData.filesCol.length < initData.files.length) {
                    const currentLength = initData.filesCol.length;
                    for (let i = currentLength; i < initData.files.length; i++) {
                        initData.filesCol.push(12);
                    }
                }
            }

            if (hasContent(initData, 'specialField1')) {
                initData.specialField1Sort = initData.specialField1Sort ?? 5 + initData.files.length;
                if (initData.specialField1Col === null) {
                    initData.specialField1Col = 12;
                }
            }

            if (hasContent(initData, 'specialField2')) {
                initData.specialField2Sort = initData.specialField2Sort ?? 6 + initData.files.length;
                if (initData.specialField2Col === null) {
                    initData.specialField2Col = 12;
                }
            }

            if (hasContent(initData, 'specialField3')) {
                initData.specialField3Sort = initData.specialField3Sort ?? 7 + initData.files.length;
                if (initData.specialField3Col === null) {
                    initData.specialField3Col = 12;
                }
            }

            if (hasContent(initData, 'specialField4')) {
                initData.specialField4Sort = initData.specialField4Sort ?? 8 + initData.files.length;
                if (initData.specialField4Col === null) {
                    initData.specialField4Col = 12;
                }
            }

            if (hasContent(initData, 'specialField5')) {
                initData.specialField5Sort = initData.specialField5Sort ?? 9 + initData.files.length;
                if (initData.specialField5Col === null) {
                    initData.specialField5Col = 12;
                }
            }

            setUpdatedData(initData);
        }
    }, [showcase])

    const handleColChange = (elementType, value) => {
        if (!updatedData) return;

        const newData = { ...updatedData };

        if (elementType.startsWith('file_')) {
            const fileIndex = parseInt(elementType.split('_')[1]);
            if (Array.isArray(newData.filesCol)) {
                const newFilesCol = [...newData.filesCol];
                newFilesCol[fileIndex] = value;
                newData.filesCol = newFilesCol;
            } else {
                newData.filesCol = Array(newData.files.length).fill(12);
                newData.filesCol[fileIndex] = value;
            }
        } else {
            newData[`${elementType}Col`] = value;
        }

        setUpdatedData(newData);
    };

    const getOrderedElements = (data) => {
        if (!data) return [];

        const elements = [];

        if (hasContent(data, 'title')) {
            elements.push({ type: 'title', sort: data.titleSort ?? 1, hasValue: true });
        }

        if (hasContent(data, 'description')) {
            elements.push({ type: 'description', sort: data.descriptionSort ?? 2, hasValue: true });
        }

        if (hasContent(data, 'content')) {
            elements.push({ type: 'content', sort: data.contentSort ?? 3, hasValue: true });
        }

        if (hasContent(data, 'files')) {
            data.files.forEach((_, index) => {
                elements.push({
                    type: `file_${index}`,
                    sort: data.filesSort && data.filesSort[index] !== undefined ? data.filesSort[index] : 4 + index,
                    hasValue: true
                });
            });
        }

        if (hasContent(data, 'specialField1')) {
            elements.push({ type: 'specialField1', sort: data.specialField1Sort ?? 5 + (data.files?.length || 0), hasValue: true });
        }

        if (hasContent(data, 'specialField2')) {
            elements.push({ type: 'specialField2', sort: data.specialField2Sort ?? 6 + (data.files?.length || 0), hasValue: true });
        }

        if (hasContent(data, 'specialField3')) {
            elements.push({ type: 'specialField3', sort: data.specialField3Sort ?? 7 + (data.files?.length || 0), hasValue: true });
        }

        if (hasContent(data, 'specialField4')) {
            elements.push({ type: 'specialField4', sort: data.specialField4Sort ?? 8 + (data.files?.length || 0), hasValue: true });
        }

        if (hasContent(data, 'specialField5')) {
            elements.push({ type: 'specialField5', sort: data.specialField5Sort ?? 9 + (data.files?.length || 0), hasValue: true });
        }

        return elements.sort((a, b) => a.sort - b.sort);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (active && over && active.id !== over.id) {
            const orderedElements = getOrderedElements(updatedData);

            const oldIndex = orderedElements.findIndex(item => item.type === active.id);
            const newIndex = orderedElements.findIndex(item => item.type === over.id);

            if (oldIndex !== -1 && newIndex !== -1) {
                const newOrder = arrayMove(orderedElements, oldIndex, newIndex);
                const newData = { ...updatedData };

                newOrder.forEach((elem, index) => {
                    if (elem.type.startsWith('file_')) {
                        const fileIndex = parseInt(elem.type.split('_')[1]);
                        if (!Array.isArray(newData.filesSort)) {
                            newData.filesSort = Array(newData.files.length).fill(0);
                        }
                        newData.filesSort[fileIndex] = index;
                    } else {
                        newData[`${elem.type}Sort`] = index;
                    }
                });

                setUpdatedData(newData);

                notification.info({
                    message: "Sıralama Güncellendi",
                    description: `${active.id} ${newIndex + 1}. sıraya taşındı`,
                    duration: 1
                });
            }
        }
    };

    const handleSaveLayout = async () => {
        if (!updatedData) return;

        setSaving(true);

        try {
            const finalData = { ...updatedData };

            if (hasContent(finalData, 'title') && finalData.titleCol === null) {
                finalData.titleCol = 12;
            }

            if (hasContent(finalData, 'description') && finalData.descriptionCol === null) {
                finalData.descriptionCol = 12;
            }

            if (hasContent(finalData, 'content') && finalData.contentCol === null) {
                finalData.contentCol = 12;
            }

            if (hasContent(finalData, 'files')) {
                if (!Array.isArray(finalData.filesCol) || finalData.filesCol.length === 0) {
                    finalData.filesCol = finalData.files.map(() => 12);
                }

                if (!Array.isArray(finalData.filesSort) || finalData.filesSort.length === 0) {
                    finalData.filesSort = finalData.files.map((_, index) => 4 + index);
                }
            }

            if (hasContent(finalData, 'specialField1') && finalData.specialField1Col === null) {
                finalData.specialField1Col = 12;
            }

            if (hasContent(finalData, 'specialField2') && finalData.specialField2Col === null) {
                finalData.specialField2Col = 12;
            }

            if (hasContent(finalData, 'specialField3') && finalData.specialField3Col === null) {
                finalData.specialField3Col = 12;
            }

            if (hasContent(finalData, 'specialField4') && finalData.specialField4Col === null) {
                finalData.specialField4Col = 12;
            }

            if (hasContent(finalData, 'specialField5') && finalData.specialField5Col === null) {
                finalData.specialField5Col = 12;
            }

            console.log("Kaydedilen veri:", finalData);
            await dispatch(fetchShowcaseDesign({
                id: finalData.id,
                filesSort: finalData.filesSort,
                filesCol: finalData.filesCol,
                filesClassname: finalData.filesClassname,
                titleSort: finalData.titleSort,
                titleCol: finalData.titleCol,
                titleClassname: finalData.titleClassname,
                descriptionSort: finalData.descriptionSort,
                descriptionCol: finalData.descriptionCol,
                descriptionClassname: finalData.descriptionClassname,
                contentSort: finalData.contentSort,
                contentCol: finalData.contentCol,
                contentClassname: finalData.contentClassname,
                specialField1Sort: finalData.specialField1Sort,
                specialField1Col: finalData.specialField1Col,
                specialField1Classname: finalData.specialField1Classname,
                specialField2Sort: finalData.specialField2Sort,
                specialField2Col: finalData.specialField2Col,
                specialField2Classname: finalData.specialField2Classname,
                specialField3Sort: finalData.specialField3Sort,
                specialField3Col: finalData.specialField3Col,
                specialField3Classname: finalData.specialField3Classname,
                specialField4Sort: finalData.specialField4Sort,
                specialField4Col: finalData.specialField4Col,
                specialField4Classname: finalData.specialField4Classname,
                specialField5Sort: finalData.specialField5Sort,
                specialField5Col: finalData.specialField5Col,
                specialField5Classname: finalData.specialField5Classname,
            }))

            getData();

            setTimeout(() => {
                notification.success({
                    message: "Tasarım Güncellendi",
                    description: "Düzen ayarları başarıyla kaydedildi."
                });
                setSaving(false);
            }, 1000);
        } catch (error) {
            notification.error({
                message: "Güncelleme Başarısız",
                description: "Düzen ayarları güncellenirken bir hata oluştu."
            });
            setSaving(false);
        }
    };

    const sortedElements = updatedData ? getOrderedElements(updatedData).map(elem => elem.type) : [];

    return (
        <CMS>
            <PageBanner title={showcase?.title[lang] || ""} description={t("showcase_design_desc")} />

            <div className="container mt-4 mb-4">
                {updatedData ? (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={sortedElements} strategy={verticalListSortingStrategy}>
                            <div className="layout-editor">
                                <div className="row">
                                    {sortedElements.map(elemType => {
                                        if (elemType.startsWith('file_')) {
                                            const fileIndex = parseInt(elemType.split('_')[1]);
                                            return (
                                                <FileElement
                                                    key={elemType}
                                                    fileId={elemType}
                                                    item={updatedData}
                                                    index={fileIndex}
                                                    onColChange={handleColChange}
                                                />
                                            );
                                        } else {
                                            return (
                                                <ContentElement
                                                    key={elemType}
                                                    elementType={elemType}
                                                    item={updatedData}
                                                    lang={lang}
                                                    onColChange={handleColChange}
                                                />
                                            );
                                        }
                                    })}
                                </div>
                            </div>
                        </SortableContext>
                        <div className='w-100 d-flex justify-content-end mt-4'>
                            <Button type="primary" onClick={handleSaveLayout} loading={saving}>{t("save_design")}</Button>
                        </div>
                    </DndContext>
                ) : (
                    <div className="alert alert-info">{t("loading_content")}...</div>
                )}
            </div>
        </CMS>
    )
}

export default PageShowcaseDesign